import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";

import { formatMoney } from "../dashboardHelpers";
import { REFUND_METHOD_LABEL, REFUND_METHOD_HELPER } from "../dashboardConstants";

// Destination-aware tail for the refund sentence: wallet credit lands
// instantly, card refunds take the gateway's settlement window.
const refundDestinationCopy = (refundMethod) =>
  refundMethod === "ONLINE" ? "will be refunded to your original card within the next 7 working days" : "will be credited to your wallet right away";

// Server-blocked cancellations — the club has to handle these at the desk.
const BLOCKED_COPY = {
  POLICY_WINDOW:
    "The free-cancellation window for this booking has passed, so it can no longer be cancelled online. Please contact the club to cancel.",
  UNPAID_INVOICE: "This booking has an open invoice and can't be cancelled online. Please contact the club.",
  CLUB_ONLY: "This booking can't be cancelled online. Please contact the club to cancel.",
  CAPTURE_IN_PROGRESS: "This booking's payment is being settled right now. Please try again in a moment."
};

function CancelPreviewCopy({ preview, currency, refundMethod }) {
  const { chargeAmount, refundAmount, packageRefund, isPaidplanCheckout, isNoCheckout, splitGame } = preview;

  // Host of a split booking whose money already settled (deadline capture ran):
  // the game is over as a game — this cancel touches only the host's own spot,
  // priced by the normal policy copy below.
  const settledSplitNote = splitGame?.isHost && splitGame?.settled && (
    <>This game has already been settled, so cancelling removes only your spot — the other players keep theirs. </>
  );

  // Host of a live split booking: cancelling dissolves the game for every
  // player. Inside the charge window the host pays the policy fee on the full
  // court price (captured from the hold); the friends are always refunded in full.
  if (splitGame?.isHost && !splitGame?.settled) {
    return (
      <>
        This is a split booking — cancelling ends the game for everyone.
        {splitGame.hostChargeAmount > 0 ? (
          <>
            {" "}
            Per the club's cancellation policy, <strong>{formatMoney(splitGame.hostChargeAmount, currency)}</strong> will be charged to your
            card, and the rest of the hold will be released.
          </>
        ) : splitGame.holdReleased ? (
          " The hold on your card will be released and you won't be charged."
        ) : (
          " You won't be charged."
        )}
        {splitGame.paidSeats > 0 && (
          <>
            {" "}
            {splitGame.paidSeats === 1 ? "The player who already paid" : `All ${splitGame.paidSeats} players who already paid`} will be refunded
            in full (<strong>{formatMoney(splitGame.seatRefundTotal, currency)}</strong> total).
          </>
        )}
      </>
    );
  }

  if (isNoCheckout) {
    return <>{settledSplitNote}No charges will be applied and nothing was paid, so there is nothing to refund.</>;
  }
  if (isPaidplanCheckout) {
    return packageRefund > 0 ? (
      <>No charges will be applied. 1 session will be refunded to your package.</>
    ) : (
      <>The session will be charged. No refund will be provided to your package.</>
    );
  }
  if (chargeAmount === 0) {
    return (
      <>
        {settledSplitNote}No charges will be applied. A total of <strong>{formatMoney(refundAmount, currency)}</strong>{" "}
        {refundDestinationCopy(refundMethod)}.
      </>
    );
  }
  if (refundAmount === 0) {
    return (
      <>
        {settledSplitNote}The full amount of <strong>{formatMoney(chargeAmount, currency)}</strong> will be charged. No refund will be
        provided.
      </>
    );
  }
  return (
    <>
      {settledSplitNote}The amount to be charged will be <strong>{formatMoney(chargeAmount, currency)}</strong>. A total of{" "}
      <strong>{formatMoney(refundAmount, currency)}</strong> {refundDestinationCopy(refundMethod)}.
    </>
  );
}

function CancelBookingDialog({ state, currency, onClose, onConfirm, onRefundMethodChange }) {
  const { open, booking, preview, refundMethod, loading, submitting } = state;

  const availableMethods = preview?.availableMethods || [];
  const isBlocked = !!preview && preview.canCancel === false;
  const hasMoneyRefund = preview && !preview.isPaidplanCheckout && preview.refundAmount > 0;
  const showChooser = hasMoneyRefund && availableMethods.length > 1;
  const showSingle = hasMoneyRefund && availableMethods.length === 1;
  const submitDisabled = loading || submitting || !preview || isBlocked || (showChooser && !refundMethod);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isBlocked ? "Cancellation Unavailable" : "Confirm Cancellation"}</DialogTitle>
      <DialogContent>
        {loading || !preview ? (
          <Box display="flex" alignItems="center" gap={2} py={1}>
            <CircularProgress size={20} />
            <DialogContentText>Calculating cancellation policy…</DialogContentText>
          </Box>
        ) : isBlocked ? (
          <DialogContentText>{BLOCKED_COPY[preview.blockedReason] || BLOCKED_COPY.POLICY_WINDOW}</DialogContentText>
        ) : (
          <>
            <DialogContentText>
              Are you sure you want to cancel{booking?.serviceName ? ` "${booking.serviceName}"` : " this booking"}?{" "}
              <CancelPreviewCopy preview={preview} currency={currency} refundMethod={refundMethod || availableMethods[0]} />
              {preview.splitGame && !preview.splitGame.isHost && preview.splitGame.status === "OPEN" && (
                <> Your spot will reopen for someone else to claim.</>
              )}
            </DialogContentText>

            {showSingle && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                Refund destination: <strong>{REFUND_METHOD_LABEL[availableMethods[0]]}</strong>.
              </Typography>
            )}

            {showChooser && (
              <FormControl sx={{ mt: 2 }}>
                <FormLabel>Where should we send the refund?</FormLabel>
                <RadioGroup value={refundMethod || ""} onChange={(e) => onRefundMethodChange(e.target.value)}>
                  {availableMethods.map((m) => (
                    <FormControlLabel
                      key={m}
                      value={m}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2">{REFUND_METHOD_LABEL[m]}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {REFUND_METHOD_HELPER[m]}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {isBlocked ? "Close" : "No"}
        </Button>
        {!isBlocked && (
          <Button onClick={onConfirm} color="error" variant="contained" disabled={submitDisabled}>
            {submitting ? <CircularProgress size={18} /> : "Yes, cancel"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default CancelBookingDialog;
